import * as React from 'react'
import {
    createContext,
    createRef,
    Dispatch,
    PropsWithChildren,
    ReactElement,
    useEffect,
    useImperativeHandle,
    useMemo,
    useReducer,
    useState,
} from 'react'
import { ServerAuthorizationTokenResponse } from '@azure/msal-common/dist/response/ServerAuthorizationTokenResponse'
import { UserDto } from '../config/userConfig'
import { jwtDecode, JwtPayload } from 'jwt-decode'
import { AccountInfo, AuthenticationResult, AuthenticationScheme, PublicClientApplication } from '@azure/msal-browser'
import { Role } from '../hooks/useRoles'
import useIdentityMethods, { IdentityMethods } from '../hooks/identity/useIdentityMethods'
import useHandleAuthenticationPhase from '../hooks/identity/useHandleAuthenticationPhase'
import useTokenInjector from '../hooks/identity/useTokenInjector'
import useAxios from '../hooks/useAxios'
import { AxiosError, AxiosResponse } from 'axios'
import useTokenErrorHandler from '../hooks/identity/useTokenErrorHandler'
import { Destination } from './AxiosContext'
import useDelayedInterval from '../hooks/useDelayedInterval'
import { DateTime } from 'luxon'
import useAcquireBootstrapToken from '../hooks/identity/useAcquireBootstrapToken'

export enum SecurityAuthority {
    Hammock = 'hammock', // Connect to HAMMOCK in the user's tenant.
    SharePoint = 'sharepoint', // Connect to MS Graph in the Hammock.nl tenant.
    Exchange = 'exchange', // Connect to MS Graph in the user's tenant.
    WebPubSub = 'webpubsub', // Connect to A Web Pub Sub socket in the user's hub.
}

export enum AuthenticationPhase {
    DiscoverUsername = 'DiscoverUsername', // Discover the username.
    SelectAccount = 'SelectAccount', // Require user interaction to discover the username.
    FetchBootstrapToken = 'FetchBootstrapToken', // Get a bootstrap token for the selected user.
    DiscoverHosts = 'DiscoverHosts', // Discover all hosts that belong to the selected user.
    SelectHost = 'SelectHost', // Require user interaction to select the host.
    FetchTokens = 'FetchTokens', // Fetch the tokens that belong to the host.
    FetchUser = 'FetchUser', // Get the user data from the host.
}

export const AuthenticationPhaseLabelMap: Map<AuthenticationPhase, string> = new Map([
    [AuthenticationPhase.DiscoverUsername, 'authentication.phase.discover.username'],
    [AuthenticationPhase.SelectAccount, 'authentication.phase.select.username'],
    [AuthenticationPhase.FetchBootstrapToken, 'authentication.phase.fetch.bootstrap'],
    [AuthenticationPhase.DiscoverHosts, 'authentication.phase.discover.hosts'],
    [AuthenticationPhase.SelectHost, 'authentication.phase.select.host'],
    [AuthenticationPhase.FetchTokens, 'authentication.phase.fetch.tokens'],
    [AuthenticationPhase.FetchUser, 'authentication.phase.fetch.user'],
])

export type Host = {
    RowKey: string
    Name: string
    Backend: string
    ClientId: string
    ClientPath: string
    DriveId: string
    ReplicationMail: string
    InsightsEnabled?: boolean
}

export type WebPubSubToken = {
    hub: string
    url: string
    issued_at: string
    expires_at: string

    access_token?: string
    refresh_token?: string
    token_type?: AuthenticationScheme
}

type Tokens = {
    [SecurityAuthority.Hammock]?: ServerAuthorizationTokenResponse
    [SecurityAuthority.SharePoint]?: ServerAuthorizationTokenResponse
    [SecurityAuthority.Exchange]?: ServerAuthorizationTokenResponse
    [SecurityAuthority.WebPubSub]?: WebPubSubToken
}

type AzureTokenPayload = {
    oid: string // UUID of user
    name: string // User's display name
    preferred_username: string // E-mail address of user
    unique_name: string // E-mail address of user
    upn: string // unified name, most often the e-mail address
    roles: Role[]
    scp: string // Space-separated scopes
    tid: string // Tenant ID
} & JwtPayload

export type PersistentIdentityContextProps = {
    username: string
    user: UserDto
    currentHost: Host
    hosts: Host[]
    tokens: Tokens
}

export type IdentityContextProps = PersistentIdentityContextProps & {
    authenticated: boolean
    roles: Role[]
    accounts: AccountInfo[]
    phase: AuthenticationPhase
    error: ReactElement | string
}

export type IdentityStateAction = {
    [K in keyof IdentityContextProps]: {
        type: K
        payload?: IdentityContextProps[K]
    }
}[keyof IdentityContextProps]

const IdentityContext = createContext<IdentityContextProps>(undefined)

const STORAGE_KEY = 'identity'
const CLIENT_ID: string = process.env.BOOTSTRAP_CLIENT_ID
const INITIAL_STATE: PersistentIdentityContextProps = {
    username: undefined,
    hosts: undefined,
    currentHost: undefined,
    tokens: {},
    user: undefined,
}

export const DestinationAuthorityMap = new Map<Destination, SecurityAuthority>([
    [Destination.Hammock, SecurityAuthority.Hammock],
    [Destination.Exchange, SecurityAuthority.Exchange],
    [Destination.Email, SecurityAuthority.Exchange],
    [Destination.PrivateEmail, SecurityAuthority.Exchange],
    [Destination.SharePoint, SecurityAuthority.SharePoint],
    [Destination.Documents, SecurityAuthority.SharePoint],
])

export const AUTHENTICATION_SCOPES: string[] = [`${process.env.BOOTSTRAP_API_URI}/access_as_user`]
export const Identity = createRef<IdentityMethods>()

export function IdentityProvider(props: PropsWithChildren): ReactElement {
    const axios = useAxios()
    const acquireBootstrapToken = useAcquireBootstrapToken()

    const [pca, setPca] = useState<PublicClientApplication>()
    const [error, setError] = useState<ReactElement | string>()
    const [phase, setPhase] = useState<AuthenticationPhase>(AuthenticationPhase.DiscoverUsername)
    const [bootstrapToken, setBootstrapToken] = useState<string>()
    const {
        setRunning: setBootstrapTimerRunning,
        delay: bootstrapTimerDelay,
        setDelay: setBootstrapTimerDelay,
    } = useDelayedInterval(undefined, () => {
        console.log('Auto refreshing bootstrap token')
        acquireBootstrapToken(state.username, pca, (token: string) => {
            if (token && token.localeCompare(bootstrapToken) === 0) {
                const diff = bootstrapTimerDelay / 2
                if (diff > 1000) {
                    console.log(
                        `Retrying auto refreshing bootstrap token in ${diff / 1000} seconds, or ${diff / 60 / 1000} minutes.`,
                    )
                    setBootstrapTimerDelay(bootstrapTimerDelay / 2)
                    return Promise.resolve()
                }
            }

            setBootstrapTimerRunning(false)
            return handleBootstrapToken(token, false)
        }).catch(() => {
            console.log('Ignoring error as this was performed in the background')
        })
    })

    // (Re)hydration of the state that is persistent in memory.
    const [state, dispatch] = useReducer(
        (state, action) => {
            switch (action.type) {
                case 'currentHost': {
                    // If the current host changes, we must also invalidate all tokens.
                    return {
                        ...state,
                        tokens: INITIAL_STATE.tokens,
                        currentHost: action.payload,
                    }
                }
                case 'username':
                    // Reset the whole state if the username has changed.
                    return {
                        ...INITIAL_STATE,
                        username: action.payload,
                    }
                case 'tokens':
                    return {
                        ...state,
                        tokens: {
                            ...state.tokens,
                            ...action.payload,
                        },
                    }
                default:
                    return { ...state, [action.type]: action.payload }
            }
        },
        INITIAL_STATE,
        () => {
            PublicClientApplication.createPublicClientApplication({
                auth: {
                    clientId: CLIENT_ID,
                },
            }).then((pca: PublicClientApplication) => {
                setPca(pca)
                pca.handleRedirectPromise().then((result: AuthenticationResult) => {
                    if (result) {
                        // We got redirected from a new login session, so store the new bootstrap token in memory.
                        handleBootstrapToken(result.accessToken)
                    }
                })
            })

            // Rehydrate from local storage.
            const savedState = localStorage.getItem(STORAGE_KEY)
            return (savedState ? JSON.parse(savedState) : INITIAL_STATE) as PersistentIdentityContextProps
        },
    ) as [PersistentIdentityContextProps, Dispatch<IdentityStateAction>]

    const handleBootstrapToken = (newBootstrapToken: string, reset: boolean = true): string => {
        // We got redirected from a new login session, so store the new bootstrap token in memory.
        setBootstrapToken(newBootstrapToken)
        const token = newBootstrapToken && jwtDecode<AzureTokenPayload>(newBootstrapToken)
        console.log(
            'Handling bootstrap token, expires at ' + (token?.exp ? new Date(token?.exp * 1000).toString() : undefined),
        )

        if (token?.exp) {
            const exp = DateTime.fromMillis(token?.exp * 1000, { zone: 'UTC' })
            const now = DateTime.now().toUTC()
            const diff = exp.toMillis() - now.toMillis() - 10 * 1000
            setBootstrapTimerDelay(diff)
            setBootstrapTimerRunning(true)
            console.log(`Auto refreshing bootstrap token after ${diff / 1000} seconds, or ${diff / 60 / 1000} minutes.`)
        }

        if (reset) {
            dispatch({
                type: 'username',
                payload:
                    (token &&
                        (token.preferred_username?.toLowerCase() ||
                            token.unique_name?.toLowerCase() ||
                            token.upn?.toLowerCase())) ||
                    undefined,
            })
            if (newBootstrapToken) {
                setPhase(AuthenticationPhase.DiscoverHosts)
            }
        }

        return newBootstrapToken
    }

    // Only proceed bootstrapping after we have fully hydrated from any persistent state.
    const hydrated = useMemo<boolean>(() => !!(state && pca && Identity.current), [state, pca, Identity.current])

    // We determine if the user is authenticated if all requirements are met:
    // - We have a hydrated state.
    // - We have a username.
    // - We have a host.
    // - We have a token (can be expired, because Axios will renew it by itself).
    // - We have discovered the user in the host.
    const authenticated = useMemo<boolean>(
        () =>
            !!(
                hydrated &&
                state.username &&
                state?.currentHost &&
                state?.tokens?.[SecurityAuthority.Hammock] &&
                state?.user
            ),
        [hydrated, state.username, state?.currentHost, state?.tokens?.[SecurityAuthority.Hammock], state?.user],
    )

    const roles = useMemo<Role[]>(
        () =>
            state.tokens[SecurityAuthority.Hammock]
                ? jwtDecode<AzureTokenPayload>(state.tokens[SecurityAuthority.Hammock].access_token)?.roles || []
                : [],
        [state.tokens?.[SecurityAuthority.Hammock]],
    )

    const handleAuthenticationPhase = useHandleAuthenticationPhase()
    const identityMethods = useIdentityMethods()
    const tokenInjector = useTokenInjector()
    const tokenErrorHandler = useTokenErrorHandler()

    // Interceptors for Axios to inject tokens.
    useEffect(() => {
        axios.interceptors.request.use(tokenInjector, Promise.reject)
        axios.interceptors.response.use(
            (value: AxiosResponse) => value,
            (error: AxiosError) => new Promise<AxiosResponse>(tokenErrorHandler(error)),
        )
    }, [])

    // Update the local storage every time the persistent state changes.
    useEffect(() => {
        if (hydrated) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
        }
    }, [hydrated, state])

    useEffect(() => {
        // Skip until we've fully hydrated.
        if (!hydrated || authenticated) {
            return
        }

        handleAuthenticationPhase({
            phase,
            setPhase,
            bootstrapToken,
            handleBootstrapToken,
            setError,
            state,
            pca,
            dispatch,
        })
    }, [hydrated, authenticated, phase])

    useImperativeHandle(Identity, () =>
        identityMethods({ state, bootstrapToken, handleBootstrapToken, pca, dispatch, setPhase, setError }),
    )

    return (
        <IdentityContext.Provider
            value={{
                ...state,
                authenticated,
                roles,
                error,
                phase,
                accounts: pca?.getAllAccounts(),
            }}
        >
            {props.children}
        </IdentityContext.Provider>
    )
}

export default IdentityContext
